import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
} from '@angular/core';
import { RolesEnum } from '@irembo-andela/irembogov3-common';
import { Subscription, catchError, finalize, map, of, timeout } from 'rxjs';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';

import { NotificationTemplate } from '../../../../../core/models/notification.model';
import { IServiceLanguage } from '../../../../../core/models/service-language.model';
import { AuthService } from '../../../../../core/services/auth.service';
import {
  CreateEmailNotificationTemplateRequest,
  CreateSmsNotificationTemplateRequest,
  CreateNotificationTemplateRequest,
  ServiceChangeRequestService,
} from '../../../../../core/services/service-change-request.service';
import {
  DEFAULT_NOTIFICATION_VARIABLES,
  NotificationEditorModel,
  NotificationEditorType,
  NotificationLocaleModel,
  NotificationVariable,
  buildNotificationArgs,
  extractNotificationVariables,
  readNotificationEditorModel,
} from '../../core/notification-editor';
import { IAction } from '../../models/workflow-builder.model';

type NotificationChannel = 'email' | 'sms';

interface TemplateCreationDraft {
  templateName: string;
  language: string;
  subject: string;
  sourceTemplateName: string | null;
  content: string;
}

interface TemplateVariableValidationError {
  variable: string;
  message: string;
}

@Component({
  selector: 'irembogov-iwb-notification-action-editor',
  templateUrl: './notification-action-editor.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class NotificationActionEditorComponent
  implements OnInit, OnChanges, OnDestroy
{
  @Input() action!: IAction;
  @Input() formFields?: unknown;
  @Input() organizationId?: string;
  @Input() argsKey?: string;

  readonly jsonEditorOptions = {
    theme: 'vs',
    language: 'json',
    minimap: { enabled: false },
    lineNumbers: 'on' as const,
    folding: true,
    automaticLayout: true,
    scrollBeyondLastLine: false,
    wordWrap: 'on' as const,
  };

  languages: IServiceLanguage[] = [];
  model: NotificationEditorModel = {
    notificationType: 'NOTIFICATION_ENGINE_TEMPLATE',
    locales: { 'en-US': {} },
  };
  selectedLocale = 'en-US';
  selectedChannel: NotificationChannel = 'email';
  variables: NotificationVariable[] = [];
  allNotificationTemplates: NotificationTemplate[] = [];
  emailTemplates: NotificationTemplate[] = [];
  smsTemplates: NotificationTemplate[] = [];
  loadingTemplates = false;
  templateError = '';
  canCreateTemplates = false;
  createTemplateVisible = false;
  creatingTemplate = false;
  createTemplateError = '';
  createTemplateSuccess = '';
  createTemplateDraft: TemplateCreationDraft = {
    templateName: '',
    language: this.selectedLocale,
    subject: '',
    sourceTemplateName: null,
    content: '',
  };
  staticTemplateSourceName: string | null = null;
  pendingStaticTemplateName: string | null = null;
  jsonMode = false;
  jsonDraft = '';
  jsonError = '';
  unsupportedLocales: string[] = [];
  localeError = '';
  localePortTargets: Record<string, string> = {};

  private readonly subscriptions = new Subscription();
  private readonly templateFetchTimeoutMs = 20000;
  private templateFetchId = 0;
  private lastPreviewFrameHtml = '';
  private trustedPreviewFrameHtml: SafeHtml | null = null;
  private readonly defaultPreviewValues: Record<string, string> = {
    accountActivationLink: 'https://irembo.gov.rw/account/activate',
    activeFeedback: 'true',
    applicantId: 'APP-0001',
    applicantName: 'Jane Applicant',
    applicantType: 'Citizen',
    applicationChannel: 'IremboGov',
    applicationEvent: 'APPLY',
    applicationId: 'APP-ID-0001',
    applicationNumber: 'APP-2026-0001',
    applicationState: 'Submitted',
    approve_date: '2026-07-21',
    approve_user: 'Service Manager',
    billReferenceNumber: 'BILL-0001',
    categoryId: 'CAT-0001',
    categoryName: 'Civil Services',
    certificate_expiration: '2026-12-31',
    changeRequestId: 'CHANGE-0001',
    creatorType: 'SERVICE_MANAGER',
    currency: 'RWF',
    dateCreated: '2026-07-21',
    emailNotificationRecipient: 'applicant@example.com',
    feedbackDate: '2026-07-21',
    feedbackSection: 'Application',
    feedbackTime: '10:30',
    formattedPrice: 'RWF 1,500',
    lastUpdated: '2026-07-21',
    locale: 'en-US',
    loginLink: 'https://irembo.gov.rw/login',
    name: 'Jane Applicant',
    officeId: 'OFFICE-0001',
    officeName: 'Head Office',
    officerState: 'PENDING_OFFICER_PROCESSING',
    oneTimePassword: '123456',
    organizationAcronym: 'ORG',
    organizationId: 'ORG-0001',
    paymentExpiration: '2026-07-28',
    paymentExpirationTime: '23:59',
    paymentMethod: 'Mobile Money',
    paymentMerchant: 'IremboPay',
    paymentStatus: 'PAID',
    price: '1500',
    priceType: 'FIXED',
    processingDeadline: '2026-08-21',
    roleName: 'Service Manager',
    serviceId: 'SERVICE-0001',
    serviceName: 'Sample Service',
    servicePaymentCode: 'SPC-0001',
    sourcePortal: 'service-management-portal',
    transactionId: 'TX-0001',
    transactionTime: '2026-07-21 10:30',
    userFullName: 'Jane Applicant',
    username: 'Jane Applicant',
    userProvidingFeedback: 'Jane Applicant',
    version: '1',
  };
  previewValues: Record<string, string> = { ...this.defaultPreviewValues };

  constructor(
    private readonly serviceChangeRequestService: ServiceChangeRequestService,
    private readonly authService: AuthService,
    private readonly cdr: ChangeDetectorRef,
    private readonly sanitizer: DomSanitizer
  ) {}

  ngOnInit(): void {
    this.serviceChangeRequestService.ensureServiceLanguagesLoaded();
    this.subscriptions.add(
      this.serviceChangeRequestService.languages.subscribe(languages => {
        const activeLanguages = languages.filter(
          language => language.status !== 'INACTIVE'
        );
        this.languages = activeLanguages.length
          ? activeLanguages
          : [this.serviceChangeRequestService.DefaultLanguage];
        this.selectedLocale = this.languages.some(
          language => language.locale === this.selectedLocale
        )
          ? this.selectedLocale
          : this.languages[0].locale;
        this.model = readNotificationEditorModel(
          buildNotificationArgs(this.model),
          this.localeKeys()
        );
        this.ensureSupportedSelectedLocale();
        this.syncLocaleIssues();
        this.cdr.markForCheck();
      })
    );
    this.loadTemplateCreatePrivileges();
    this.loadTemplates('');
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (!this.action) return;
    if (!changes['action'] && !changes['argsKey'] && !changes['formFields']) {
      return;
    }
    this.model = readNotificationEditorModel(
      this.notificationArgsSource(),
      this.localeKeys()
    );
    this.variables = extractNotificationVariables(this.formFields);
    this.jsonMode = false;
    this.jsonError = '';
    this.ensureSupportedSelectedLocale();
    this.syncLocaleIssues();
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  localeKeys(): string[] {
    return this.languages.length
      ? this.languages.map(language => language.locale)
      : ['en-US'];
  }

  missingNotificationLocaleLanguages(): IServiceLanguage[] {
    const languages = this.languages.length
      ? this.languages
      : [this.serviceChangeRequestService.DefaultLanguage];
    const hasAnyConfiguredLanguage = languages.some(language =>
      this.hasNotificationContent(this.model, language.locale)
    );
    if (!hasAnyConfiguredLanguage) return [];
    return languages.filter(
      language => !this.hasNotificationContent(this.model, language.locale)
    );
  }

  configuredNotificationLocaleLanguages(): IServiceLanguage[] {
    const languages = this.languages.length
      ? this.languages
      : [this.serviceChangeRequestService.DefaultLanguage];
    return languages.filter(language =>
      this.hasNotificationContent(this.model, language.locale)
    );
  }

  missingNotificationLocaleMessage(): string {
    return this.missingNotificationLocaleLanguages()
      .map(
        language => `${language.name || language.locale} (${language.locale})`
      )
      .join(', ');
  }

  configuredNotificationLocaleMessage(): string {
    return this.configuredNotificationLocaleLanguages()
      .map(
        language => `${language.name || language.locale} (${language.locale})`
      )
      .join(', ');
  }

  draft(locale = this.selectedLocale): NotificationLocaleModel {
    if (!this.model.locales[locale]) this.model.locales[locale] = {};
    return this.model.locales[locale];
  }

  selectLocale(locale: string): void {
    this.selectedLocale = locale;
    if (!this.createTemplateDraft.language) {
      this.createTemplateDraft.language = locale;
    }
  }

  setType(type: NotificationEditorType): void {
    this.model.notificationType = type;
  }

  setChannel(channel: NotificationChannel): void {
    this.selectedChannel = channel;
    this.staticTemplateSourceName = null;
    this.pendingStaticTemplateName = null;
    this.createTemplateError = '';
    this.createTemplateSuccess = '';
  }

  insertVariable(value: string): void {
    if (this.model.notificationType !== 'STATIC') return;
    const draft = this.draft();
    if (this.selectedChannel === 'email') {
      draft.emailBody = `${draft.emailBody || ''}${value}`;
    } else {
      draft.smsBody = `${draft.smsBody || ''}${value}`;
    }
  }

  setDraftValue(
    key: keyof NotificationLocaleModel,
    value: string | null
  ): void {
    this.draft()[key] = value;
  }

  setLocalePortTarget(locale: string, targetLocale: string): void {
    this.localePortTargets[locale] = targetLocale;
  }

  portUnsupportedLocale(locale: string): void {
    const targetLocale =
      this.localePortTargets[locale] ||
      this.selectedLocale ||
      this.localeKeys()[0];
    if (
      !this.localeKeys().includes(targetLocale) ||
      !this.model.locales[locale]
    ) {
      return;
    }

    this.model.locales[targetLocale] = {
      ...(this.model.locales[targetLocale] || {}),
      ...this.model.locales[locale],
    };
    delete this.model.locales[locale];
    delete this.localePortTargets[locale];
    this.selectedLocale = targetLocale;
    this.syncLocaleIssues();
  }

  refreshTemplates(): void {
    this.loadTemplates('');
  }

  loadTemplates(searchTerm: string): void {
    const requestId = (this.templateFetchId += 1);
    this.loadingTemplates = true;
    this.templateError = '';
    this.cdr.markForCheck();

    this.subscriptions.add(
      this.serviceChangeRequestService
        .getAllNotificationTemplates(this.templateListQuery())
        .pipe(
          timeout(this.templateFetchTimeoutMs),
          map(response => ({
            templates: this.normalizeTemplateResponse(response),
            error: '',
          })),
          catchError(() =>
            of({
              templates: this.allNotificationTemplates,
              error: 'Could not load notification templates.',
            })
          ),
          finalize(() => {
            if (requestId !== this.templateFetchId) return;
            this.loadingTemplates = false;
            this.cdr.markForCheck();
          })
        )
        .subscribe(({ templates, error }) => {
          if (requestId !== this.templateFetchId) return;
          this.allNotificationTemplates = templates;
          this.templateError = error;
          this.applyTemplateFilter(searchTerm);
          this.cdr.markForCheck();
        })
    );
  }

  templateListQuery(): string {
    return 'page=0&pageSize=1000';
  }

  applyTemplateFilter(searchTerm: string): void {
    const cleanTerm = (searchTerm || '').trim().toLowerCase();
    const matchesSearch = (template: NotificationTemplate): boolean => {
      if (!cleanTerm) return true;
      return [
        template.templateName,
        template.language,
        template.subject,
        template.template,
      ].some(value =>
        String(value || '')
          .toLowerCase()
          .includes(cleanTerm)
      );
    };
    this.emailTemplates = this.allNotificationTemplates.filter(
      template => template.type === 'EMAIL' && matchesSearch(template)
    );
    this.smsTemplates = this.allNotificationTemplates.filter(
      template => template.type === 'SMS' && matchesSearch(template)
    );
    this.cdr.markForCheck();
  }

  openCreateTemplate(): void {
    if (!this.canCreateTemplates) return;
    this.createTemplateVisible = true;
    this.createTemplateError = '';
    this.createTemplateSuccess = '';
    this.createTemplateDraft = {
      ...this.createTemplateDraft,
      language: this.createTemplateDraft.language || this.selectedLocale,
    };
  }

  closeCreateTemplate(): void {
    this.createTemplateVisible = false;
    this.createTemplateError = '';
  }

  selectCreateTemplateSource(templateName: string | null): void {
    this.createTemplateDraft.sourceTemplateName = templateName;
    const template = this.templateByName(
      this.selectedChannel,
      templateName,
      this.createTemplateDraft.language
    );
    if (!template) return;

    this.createTemplateDraft = {
      ...this.createTemplateDraft,
      subject:
        this.selectedChannel === 'email'
          ? template.subject || this.createTemplateDraft.subject
          : '',
      content: template.template || template.stringifiedTemplate || '',
    };
  }

  templatesForSelectedChannel(): NotificationTemplate[] {
    return this.selectedChannel === 'email'
      ? this.emailTemplates
      : this.smsTemplates;
  }

  selectStaticTemplateSource(templateName: string | null): void {
    this.staticTemplateSourceName = templateName;
    this.pendingStaticTemplateName = templateName;
    this.cdr.markForCheck();
  }

  confirmStaticTemplateSource(): void {
    const template = this.templateByName(
      this.selectedChannel,
      this.pendingStaticTemplateName,
      this.selectedLocale
    );
    if (!template) {
      this.cancelStaticTemplateSource();
      return;
    }

    const content = template.template || template.stringifiedTemplate || '';
    if (this.selectedChannel === 'email') {
      this.setDraftValue('subject', template.subject || '');
      this.setDraftValue('emailBody', content);
    } else {
      this.setDraftValue('smsBody', content);
    }
    this.pendingStaticTemplateName = null;
    this.cdr.markForCheck();
  }

  cancelStaticTemplateSource(): void {
    this.staticTemplateSourceName = null;
    this.pendingStaticTemplateName = null;
    this.cdr.markForCheck();
  }

  saveNewTemplate(): void {
    if (!this.canCreateTemplates || this.creatingTemplate) return;
    this.createTemplateError = '';
    this.createTemplateSuccess = '';

    const templateName = this.createTemplateDraft.templateName.trim();
    const language = this.createTemplateDraft.language || this.selectedLocale;
    const content = this.createTemplateDraft.content.trim();
    const subject = this.createTemplateDraft.subject.trim();

    if (!templateName) {
      this.createTemplateError = 'Template name is required.';
      return;
    }
    if (!language) {
      this.createTemplateError = 'Language is required.';
      return;
    }
    if (!content) {
      this.createTemplateError = 'Template content is required.';
      return;
    }
    if (this.selectedChannel === 'email' && !subject) {
      this.createTemplateError = 'Email subject is required.';
      return;
    }

    if (this.templateExists(this.selectedChannel, templateName, language)) {
      this.selectSavedTemplate(this.selectedChannel, templateName);
      this.createTemplateError =
        'A template with this name and language already exists. It has been selected instead.';
      return;
    }

    const templateType = this.organizationId
      ? 'ORGANIZATION_SERVICE'
      : 'PLATFORM';
    const basePayload: CreateSmsNotificationTemplateRequest = {
      templateName,
      language,
      template: content,
      status: 'Active',
      templateType,
      ...(this.organizationId ? { organizationId: this.organizationId } : {}),
    };

    let payload:
      | CreateEmailNotificationTemplateRequest
      | CreateSmsNotificationTemplateRequest;
    let request:
      | ReturnType<
          ServiceChangeRequestService['createEmailNotificationTemplate']
        >
      | ReturnType<
          ServiceChangeRequestService['createSmsNotificationTemplate']
        >;
    if (this.selectedChannel === 'email') {
      payload = {
        ...basePayload,
        subject,
      };
      request =
        this.serviceChangeRequestService.createEmailNotificationTemplate(
          payload
        );
    } else {
      payload = basePayload;
      request =
        this.serviceChangeRequestService.createSmsNotificationTemplate(payload);
    }

    this.creatingTemplate = true;
    this.subscriptions.add(
      request.pipe(finalize(() => (this.creatingTemplate = false))).subscribe({
        next: response => {
          const created = this.createdTemplateFromResponse(
            response,
            payload,
            this.selectedChannel
          );
          this.addTemplate(this.selectedChannel, created);
          this.selectSavedTemplate(this.selectedChannel, created.templateName);
          this.createTemplateSuccess = 'Template saved and selected.';
          this.createTemplateVisible = false;
          this.createTemplateDraft = {
            templateName: '',
            language,
            subject: '',
            sourceTemplateName: null,
            content: '',
          };
          this.loadTemplates('');
          this.cdr.markForCheck();
        },
        error: () => {
          this.createTemplateError = 'Could not save the template.';
          this.cdr.markForCheck();
        },
      })
    );
  }

  selectedTemplate(
    channel = this.selectedChannel
  ): NotificationTemplate | null {
    const templateName =
      channel === 'email'
        ? this.draft().emailTemplateName
        : this.draft().smsTemplateName;
    const templates =
      channel === 'email' ? this.emailTemplates : this.smsTemplates;
    return (
      templates.find(
        template =>
          template.templateName === templateName &&
          (!template.language || template.language === this.selectedLocale)
      ) ||
      templates.find(template => template.templateName === templateName) ||
      null
    );
  }

  previewTitle(): string {
    return this.renderPreviewTemplate(this.rawPreviewTitle());
  }

  previewBody(): string {
    return this.renderPreviewTemplate(this.rawPreviewBody());
  }

  previewFrameHtml(): string {
    const body = this.previewBody();
    if (!body) {
      return this.wrapPreviewHtml('No preview content yet.');
    }
    if (/<html[\s>]/i.test(body) || /<!doctype/i.test(body)) {
      return this.withResponsivePreviewStyles(body);
    }
    return this.wrapPreviewHtml(body);
  }

  previewFrameSrcdoc(): SafeHtml {
    const html = this.previewFrameHtml();
    if (html !== this.lastPreviewFrameHtml || !this.trustedPreviewFrameHtml) {
      this.lastPreviewFrameHtml = html;
      this.trustedPreviewFrameHtml =
        this.sanitizer.bypassSecurityTrustHtml(html);
    }
    return this.trustedPreviewFrameHtml;
  }

  previewVariableIssues(): string[] {
    return this.missingPreviewVariables();
  }

  templateVariableErrors(): TemplateVariableValidationError[] {
    const knownVariables = this.knownTemplateVariableNames();
    return this.templateVariableNames()
      .filter(variable => !knownVariables.has(variable))
      .map(variable => ({
        variable,
        message: 'is not available from the form or global service variables.',
      }));
  }

  templateSyntaxErrors(): string[] {
    const template = this.rawTemplateForPreview();
    const errors = new Set<string>();
    const freemarkerStartPattern = /\$\{/g;
    let match: RegExpExecArray | null;

    while ((match = freemarkerStartPattern.exec(template))) {
      const start = match.index;
      const closeIndex = template.indexOf('}', start + 2);
      const nextTagIndex = template.indexOf('<', start + 2);
      const malformed =
        closeIndex === -1 || (nextTagIndex !== -1 && nextTagIndex < closeIndex);
      if (!malformed) continue;

      const end = nextTagIndex !== -1 ? nextTagIndex : template.length;
      errors.add(
        `Malformed Freemarker placeholder: ${template.slice(start, end).trim()}`
      );
    }

    const moustachePattern = /\{\{\s*([A-Za-z0-9_.$-]+)\s*\}\}/g;
    while ((match = moustachePattern.exec(template))) {
      errors.add(`Use Freemarker syntax \${${match[1]}}, not {{${match[1]}}}.`);
    }

    return [...errors];
  }

  missingPreviewVariables(): string[] {
    const knownVariables = this.knownTemplateVariableNames();
    return this.templateVariableNames().filter(
      variable =>
        knownVariables.has(variable) && !this.previewValueFor(variable).trim()
    );
  }

  setPreviewValue(variable: string, value: string): void {
    this.previewValues = {
      ...this.previewValues,
      [variable]: value,
    };
    this.lastPreviewFrameHtml = '';
    this.trustedPreviewFrameHtml = null;
  }

  previewValueFor(variable: string): string {
    return this.previewValues[variable] || '';
  }

  previewLabelFor(variable: string): string {
    return (
      this.previewVariables().find(item => this.variableName(item) === variable)
        ?.label || variable
    );
  }

  private rawPreviewTitle(): string {
    if (this.model.notificationType === 'STATIC') {
      return this.draft().subject || this.draft().title || '';
    }
    return this.selectedTemplate()?.subject || '';
  }

  private rawPreviewBody(): string {
    if (this.model.notificationType === 'STATIC') {
      return this.selectedChannel === 'email'
        ? this.draft().emailBody || ''
        : this.draft().smsBody || '';
    }
    return this.selectedTemplate()?.template || '';
  }

  private renderPreviewTemplate(template: string): string {
    if (!template) return '';
    const knownVariables = this.knownTemplateVariableNames();
    return template.replace(
      /\$\{\s*([A-Za-z0-9_.$-]+)(?:\s*![^}]*)?\s*\}/g,
      (match: string, key?: string) => {
        if (!key || !knownVariables.has(key)) return match;
        const value = key ? this.previewValueFor(key) : '';
        return value.trim() ? value : match;
      }
    );
  }

  private rawTemplateForPreview(): string {
    return `${this.rawPreviewTitle()} ${this.rawPreviewBody()}`;
  }

  private templateVariableNames(): string[] {
    if (this.templateSyntaxErrors().length) return [];
    return this.extractTemplateVariableNames(this.rawTemplateForPreview());
  }

  private extractTemplateVariableNames(template: string): string[] {
    const variables = new Set<string>();
    const variablePattern = /\$\{\s*([A-Za-z0-9_.$-]+)(?:\s*![^}]*)?\s*\}/g;
    let match: RegExpExecArray | null;
    while ((match = variablePattern.exec(template))) {
      variables.add(match[1]);
    }
    return [...variables];
  }

  private knownTemplateVariableNames(): Set<string> {
    return new Set(
      this.previewVariables().map(variable => this.variableName(variable))
    );
  }

  private previewVariables(): NotificationVariable[] {
    return this.variables.length
      ? this.variables
      : DEFAULT_NOTIFICATION_VARIABLES;
  }

  private variableName(variable: NotificationVariable): string {
    const match = variable.value.match(
      /^\$\{\s*([A-Za-z0-9_.$-]+)(?:\s*![^}]*)?\s*\}$/
    );
    return match?.[1] || variable.label;
  }

  private wrapPreviewHtml(body: string): string {
    return `<!doctype html><html><head><base target="_blank">${this.responsivePreviewHeadMarkup()}</head><body>${body}</body></html>`;
  }

  private withResponsivePreviewStyles(html: string): string {
    if (html.includes('iwb-email-preview-responsive')) return html;
    const headMarkup = `<base target="_blank">${this.responsivePreviewHeadMarkup()}`;
    if (/<head[\s>]/i.test(html)) {
      return html.replace(/<head([^>]*)>/i, `<head$1>${headMarkup}`);
    }
    if (/<html[\s>]/i.test(html)) {
      return html.replace(
        /<html([^>]*)>/i,
        `<html$1><head>${headMarkup}</head>`
      );
    }
    return this.wrapPreviewHtml(html);
  }

  private responsivePreviewHeadMarkup(): string {
    return [
      '<meta name="viewport" content="width=device-width, initial-scale=1">',
      '<style class="iwb-email-preview-responsive">',
      'html,body{margin:0;max-width:100%;overflow-x:hidden;}',
      'body{font-family:Arial,sans-serif;color:#344054;line-height:1.5;word-break:break-word;}',
      '*{box-sizing:border-box;}',
      'table{max-width:100%!important;width:100%!important;table-layout:auto!important;}',
      'td,th{max-width:100%!important;overflow-wrap:anywhere;}',
      'img{max-width:100%!important;height:auto!important;}',
      'pre,code{white-space:pre-wrap;overflow-wrap:anywhere;}',
      '</style>',
    ].join('');
  }

  setMode(jsonMode: boolean): void {
    if (jsonMode === this.jsonMode) return;
    if (jsonMode) {
      this.jsonDraft = JSON.stringify(
        buildNotificationArgs(this.model),
        null,
        2
      );
      this.jsonError = '';
      this.jsonMode = true;
      return;
    }
    try {
      const parsed = JSON.parse(this.jsonDraft || '{}');
      this.model = readNotificationEditorModel(parsed, this.localeKeys());
      this.ensureSupportedSelectedLocale();
      this.syncLocaleIssues();
      this.jsonError = '';
      this.jsonMode = false;
    } catch {
      this.jsonError = 'Invalid JSON. Fix it before returning to Design.';
    }
  }

  currentArgs(): Record<string, unknown> | null | undefined {
    if (this.jsonMode) {
      try {
        const parsed = JSON.parse(this.jsonDraft || '{}');
        const parsedModel = readNotificationEditorModel(
          parsed && typeof parsed === 'object'
            ? (parsed as Record<string, unknown>)
            : {},
          this.localeKeys()
        );
        const unsupported = this.findUnsupportedLocales(parsedModel);
        if (unsupported.length) {
          this.jsonError = this.unsupportedLocaleMessage(unsupported);
          this.cdr.markForCheck();
          return undefined;
        }
        const args = parsed && typeof parsed === 'object' ? parsed : null;
        return this.wrapNotificationArgs(args);
      } catch {
        return this.wrapNotificationArgs(buildNotificationArgs(this.model));
      }
    }
    this.syncLocaleIssues();
    if (this.unsupportedLocales.length) {
      this.cdr.markForCheck();
      return undefined;
    }
    return this.wrapNotificationArgs(buildNotificationArgs(this.model));
  }

  private notificationArgsSource(): Record<string, unknown> {
    const actionArgs = this.action?.args || {};
    if (!this.argsKey) return actionArgs;
    const nested = actionArgs[this.argsKey];
    if (typeof nested === 'string' && nested.trim()) {
      try {
        const parsed = JSON.parse(nested);
        return parsed && typeof parsed === 'object' && !Array.isArray(parsed)
          ? (parsed as Record<string, unknown>)
          : {};
      } catch {
        return {};
      }
    }
    return nested && typeof nested === 'object' && !Array.isArray(nested)
      ? (nested as Record<string, unknown>)
      : {};
  }

  private wrapNotificationArgs(
    args: Record<string, unknown> | null
  ): Record<string, unknown> | null {
    return this.argsKey ? { [this.argsKey]: args } : args;
  }

  private loadTemplateCreatePrivileges(): void {
    this.authService
      .loadRawUserRoles()
      .then(roles => {
        const roleNames = roles.map(role => String(role));
        this.canCreateTemplates =
          roleNames.includes(RolesEnum.ROLE_IREMBOGOV_ADMIN) ||
          roleNames.includes('ROLE_IREMBO_ADMIN');
        this.cdr.markForCheck();
      })
      .catch(() => {
        this.canCreateTemplates = false;
        this.cdr.markForCheck();
      });
  }

  private normalizeTemplateResponse(response: unknown): NotificationTemplate[] {
    const data = (response as { data?: unknown })?.data;
    if (Array.isArray(data)) return data as NotificationTemplate[];
    if (
      data &&
      typeof data === 'object' &&
      Array.isArray((data as { templates?: unknown }).templates)
    ) {
      return (data as { templates: NotificationTemplate[] }).templates;
    }
    if (
      data &&
      typeof data === 'object' &&
      Array.isArray((data as { content?: unknown }).content)
    ) {
      return (data as { content: NotificationTemplate[] }).content;
    }
    if (Array.isArray((response as { content?: unknown })?.content)) {
      return (response as { content: NotificationTemplate[] }).content;
    }
    return [];
  }

  private templateByName(
    channel: NotificationChannel,
    templateName: string | null,
    locale = this.createTemplateDraft.language || this.selectedLocale
  ): NotificationTemplate | null {
    if (!templateName) return null;
    const templates =
      channel === 'email' ? this.emailTemplates : this.smsTemplates;
    return (
      templates.find(
        template =>
          template.templateName === templateName &&
          (!template.language || template.language === locale)
      ) ||
      templates.find(template => template.templateName === templateName) ||
      null
    );
  }

  private templateExists(
    channel: NotificationChannel,
    templateName: string,
    language: string
  ): boolean {
    const templates =
      channel === 'email' ? this.emailTemplates : this.smsTemplates;
    return templates.some(
      template =>
        template.templateName?.toLowerCase() === templateName.toLowerCase() &&
        (template.language || '').toLowerCase() === language.toLowerCase()
    );
  }

  private addTemplate(
    channel: NotificationChannel,
    template: NotificationTemplate
  ): void {
    const templates =
      channel === 'email' ? this.emailTemplates : this.smsTemplates;
    const next = [
      template,
      ...templates.filter(
        item =>
          !(
            item.templateName === template.templateName &&
            item.language === template.language
          )
      ),
    ];
    if (channel === 'email') {
      this.emailTemplates = next;
    } else {
      this.smsTemplates = next;
    }
  }

  private selectSavedTemplate(
    channel: NotificationChannel,
    templateName: string
  ): void {
    this.setType('NOTIFICATION_ENGINE_TEMPLATE');
    if (channel === 'email') {
      this.setDraftValue('emailTemplateName', templateName);
    } else {
      this.setDraftValue('smsTemplateName', templateName);
    }
  }

  private createdTemplateFromResponse(
    response: unknown,
    payload: CreateNotificationTemplateRequest,
    channel: NotificationChannel
  ): NotificationTemplate {
    const data = (
      response as {
        data?: Partial<NotificationTemplate> & { message?: string };
      }
    )?.data;
    return {
      id:
        data?.id ||
        data?.templateId ||
        this.extractCreatedTemplateId(data?.message) ||
        '',
      templateId: data?.templateId,
      type: channel === 'email' ? 'EMAIL' : 'SMS',
      templateName: data?.templateName || payload.templateName,
      language: data?.language || payload.language,
      subject:
        data?.subject || ('subject' in payload ? payload.subject : '') || '',
      template: data?.template || payload.template,
      status: data?.status || payload.status,
      organizationId: data?.organizationId || payload.organizationId,
      templateType: data?.templateType || payload.templateType,
      stringifiedTemplate: data?.stringifiedTemplate,
    };
  }

  private extractCreatedTemplateId(message?: string): string {
    return message?.match(/Id:\s*([^.\s]+)/i)?.[1] || '';
  }

  private ensureSupportedSelectedLocale(): void {
    const supportedLocales = this.localeKeys();
    if (!supportedLocales.length) return;
    if (!supportedLocales.includes(this.selectedLocale)) {
      this.selectedLocale = supportedLocales[0];
    }
    if (!this.model.locales[this.selectedLocale]) {
      this.model.locales[this.selectedLocale] = {};
    }
  }

  private syncLocaleIssues(): void {
    this.unsupportedLocales = this.findUnsupportedLocales(this.model);
    this.localeError = this.unsupportedLocales.length
      ? this.unsupportedLocaleMessage(this.unsupportedLocales)
      : '';

    const supportedLocales = this.localeKeys();
    this.unsupportedLocales.forEach(locale => {
      if (
        !this.localePortTargets[locale] ||
        !supportedLocales.includes(this.localePortTargets[locale])
      ) {
        this.localePortTargets[locale] =
          this.selectedLocale || supportedLocales[0] || 'en-US';
      }
    });
    Object.keys(this.localePortTargets).forEach(locale => {
      if (!this.unsupportedLocales.includes(locale)) {
        delete this.localePortTargets[locale];
      }
    });
  }

  private findUnsupportedLocales(model: NotificationEditorModel): string[] {
    const supportedLocales = new Set(this.localeKeys());
    return Object.keys(model.locales).filter(
      locale =>
        !supportedLocales.has(locale) &&
        this.hasNotificationContent(model, locale)
    );
  }

  private hasNotificationContent(
    model: NotificationEditorModel,
    locale: string
  ): boolean {
    const value = model.locales[locale];
    return Object.values(value || {}).some(
      item => item !== null && item !== ''
    );
  }

  private unsupportedLocaleMessage(locales: string[]): string {
    return `Unsupported notification locale${
      locales.length > 1 ? 's' : ''
    }: ${locales.join(
      ', '
    )}. These templates will not be sent until they are ported to an active system language.`;
  }
}
